import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useBooking } from "../context/BookingContext";
import { usePricing, isWeekend, isNightSlot } from "../context/PricingContext";
import { usePromo } from "../context/PromoContext";
import { validatePartnerCode, getPartnerDiscount } from "../utils/partnerCodes";

function formatTime12(t) {
  const [h, m] = t.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${period}`;
}

function slotEndTime(t) {
  const [h, m] = t.split(":").map(Number);
  const endH = (h + 1) % 24;
  return `${String(endH).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function slotHour(t) {
  const h = parseInt(t, 10);
  return h < 5 ? h + 24 : h;
}

const STEPS = [
  { n: 1, label: "Select Sport" },
  { n: 2, label: "Choose Slots" },
  { n: 3, label: "Your Details" },
  { n: 4, label: "Payment"      },
];

const SESSION_KEY = "tsa_payment_state";

function loadPaymentSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Date.now() - (data.savedAt || 0) > 30 * 60 * 1000) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    return data;
  } catch { return null; }
}

export default function BookingForm() {
  const navigate = useNavigate();
  const {
    selectedDate, setSelectedDate,
    selectedSlots, setSelectedSlots,
    bookingForm, setBookingForm,
    calcTotal,
    selectedSport, setSelectedSport,
    partnerCode, setPartnerCode,
  } = useBooking();
  const { getSportRate } = usePricing();
  const { calcDiscount } = usePromo();
  const [errors, setErrors] = useState({});
  const [codeInput, setCodeInput] = useState(partnerCode || "");
  const [codeStatus, setCodeStatus] = useState(partnerCode ? "valid" : "idle");

  // True if sessionStorage has saved slots — prevents guard from firing during restore
  const [hasSession] = useState(() => {
    const saved = loadPaymentSession();
    return Array.isArray(saved?.selectedSlots) && saved.selectedSlots.length > 0;
  });
  const restoringRef = useRef(false);

  // Restore booking state from sessionStorage if React state was wiped (e.g. page refresh)
  useEffect(() => {
    if (selectedSlots.length > 0) return;
    const saved = loadPaymentSession();
    if (!saved?.selectedSlots?.length) return;

    console.log("[Session] BookingForm: restoring state from sessionStorage");
    restoringRef.current = true;
    setSelectedSlots(saved.selectedSlots);
    if (saved.selectedSport) setSelectedSport(saved.selectedSport);
    if (saved.selectedDate)  setSelectedDate(saved.selectedDate);
    if (saved.bookingForm)   setBookingForm(saved.bookingForm);
    if (saved.partnerCode !== undefined) setPartnerCode(saved.partnerCode);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dayRate        = getSportRate(selectedDate, selectedSport?.name);
  const weekend        = isWeekend(selectedDate);
  const daySlots       = selectedSlots.filter((s) => !isNightSlot(s));
  const nightSlots     = selectedSlots.filter((s) =>  isNightSlot(s));
  const rawTotal       = calcTotal();
  const promoDiscount  = calcDiscount(rawTotal, selectedSlots.length, dayRate);
  const afterPromo     = rawTotal - promoDiscount;
  const partnerDiscount = getPartnerDiscount(partnerCode, afterPromo);
  const discount       = promoDiscount + partnerDiscount;
  const total          = rawTotal - discount;

  const handleApplyCode = () => {
    const valid = validatePartnerCode(codeInput);
    if (valid) {
      setPartnerCode(valid);
      setCodeStatus("valid");
      setCodeInput(valid);
    } else {
      setCodeStatus("invalid");
    }
  };

  const handleRemoveCode = () => {
    setPartnerCode(null);
    setCodeInput("");
    setCodeStatus("idle");
  };

  // Don't redirect if we're restoring from sessionStorage (restoringRef) or
  // the session check already found saved slots (hasSession) — state is incoming.
  if (!selectedSlots.length && !hasSession && !restoringRef.current) { navigate("/book"); return null; }

  const validate = () => {
    const e = {};
    if (!bookingForm.name.trim()) e.name = "Name is required";
    if (!/^[6-9]\d{9}$/.test(bookingForm.phone)) e.phone = "Enter a valid 10-digit phone number";
    return e;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    navigate("/payment-summary");
  };

  const sorted    = [...selectedSlots].sort((a, b) => slotHour(a) - slotHour(b));
  const hasNextDay = sorted.some((s) => parseInt(s, 10) < 5);
  const dateLabel = new Date(selectedDate + "T00:00:00").toLocaleDateString("en-IN", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
  const nextDayLabel = (() => {
    if (!hasNextDay || !selectedDate) return "";
    const d = new Date(selectedDate + "T00:00:00");
    d.setDate(d.getDate() + 1);
    return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "long" });
  })();

  return (
    <div className="bg-white" style={{ paddingTop: 64 }}>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>

          {/* ── Progress indicator ── */}
          <div className="flex items-center mb-10">
            {STEPS.map((step, i) => {
              const done    = step.n < 3;
              const current = step.n === 3;
              return (
                <div key={step.n} className="flex items-center flex-1 last:flex-none">
                  {/* Node */}
                  <div className="flex flex-col items-center gap-1 flex-shrink-0">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black transition-colors ${
                      done    ? "bg-[#f59e0b] text-black"
                      : current ? "bg-black text-white"
                      : "bg-black/8 text-black/30"
                    }`}>
                      {done ? "✓" : step.n}
                    </div>
                    <span className={`text-[10px] font-semibold whitespace-nowrap hidden sm:block ${
                      done ? "text-[#b45309]" : current ? "text-black" : "text-black/30"
                    }`}>
                      {step.label}
                    </span>
                  </div>
                  {/* Connector — not after last step */}
                  {i < STEPS.length - 1 && (
                    <div className={`flex-1 h-px mx-2 mb-4 ${done ? "bg-[#f59e0b]" : "bg-black/8"}`} />
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Page title ── */}
          <h1
            className="text-black font-black text-3xl sm:text-4xl tracking-tight mb-1"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Your Details
          </h1>
          <p className="text-black/40 text-sm mb-7">Almost there — fill in your team info below</p>

          {/* ── Booking summary card ── */}
          <div
            className="rounded-2xl p-5 mb-7 relative overflow-hidden"
            style={{ background: "#1a1a1a" }}
          >
            <div className="relative">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-[#f59e0b] text-[10px] font-black tracking-[0.2em] uppercase mb-1 opacity-80">Booking Summary</p>
                  <p className="text-white font-bold text-base leading-tight">{dateLabel}</p>
                  <p className="text-white/50 text-sm mt-1">
                    {formatTime12(sorted[0])} → {formatTime12(slotEndTime(sorted[sorted.length - 1]))}
                    {hasNextDay && <span className="text-white/30 text-xs ml-1">({nextDayLabel})</span>}
                    <span className="mx-1.5 text-white/20">·</span>
                    {sorted.length} hr{sorted.length > 1 ? "s" : ""}
                    <span className="mx-1.5 text-white/20">·</span>
                    {sorted.length} slot{sorted.length > 1 ? "s" : ""}
                  </p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                      weekend ? "bg-[#f59e0b]/20 text-[#f59e0b]" : "bg-white/10 text-white/50"
                    }`}>
                      {weekend ? "⚡ Weekend" : "📅 Weekday"}
                    </span>
                    {daySlots.length > 0 && (
                      <span className="inline-flex items-center gap-1 bg-amber-500/20 rounded-full px-2.5 py-1 text-xs text-amber-200 font-semibold">
                        ☀️ {daySlots.length} day
                      </span>
                    )}
                    {nightSlots.length > 0 && (
                      <span className="inline-flex items-center gap-1 bg-slate-700/40 rounded-full px-2.5 py-1 text-xs text-slate-300 font-semibold">
                        🌙 {nightSlots.length} night
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  {discount > 0 && (
                    <p className="text-white/25 text-xs line-through leading-none mb-0.5">
                      ₹{rawTotal.toLocaleString()}
                    </p>
                  )}
                  <p className="text-[#f59e0b] font-black text-3xl leading-none tracking-tight">
                    ₹{total.toLocaleString()}
                  </p>
                  {discount > 0 && (
                    <p className="text-green-400 text-xs mt-1 font-medium">🎁 Saving ₹{discount.toLocaleString()}</p>
                  )}
                  {partnerCode && (
                    <p className="text-purple-300 text-xs mt-0.5 font-medium">{partnerCode} applied</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Form ── */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {[
              { key: "name",  label: "Full Name",    placeholder: "Virat Kohli", type: "text" },
              { key: "phone", label: "Phone Number", placeholder: "9876543210",  type: "tel"  },
            ].map(({ key, label, placeholder, type }) => (
              <div key={key}>
                <label className="block text-black font-bold text-sm mb-1.5">{label}</label>
                <input
                  type={type}
                  placeholder={placeholder}
                  className={`w-full bg-black/[0.04] border rounded-xl px-4 py-3 text-black placeholder-black/25 text-sm focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/40 focus:border-[#f59e0b]/60 transition-all ${
                    errors[key] ? "border-red-300 bg-red-50" : "border-black/8"
                  }`}
                  value={bookingForm[key]}
                  onChange={(e) => {
                    setBookingForm((f) => ({ ...f, [key]: e.target.value }));
                    setErrors((er) => ({ ...er, [key]: undefined }));
                  }}
                />
                {errors[key] && <p className="text-red-500 text-xs mt-1.5 font-medium">{errors[key]}</p>}
              </div>
            ))}

            {/* ── Partner promo code ── */}
            <div>
              <label className="block text-black font-bold text-sm mb-1.5">Have a promo code?</label>
              {codeStatus === "valid" ? (
                <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-green-600 text-sm">✓</span>
                    <span className="text-green-700 font-semibold text-sm">{partnerCode}</span>
                    <span className="text-green-600 text-sm">— 15% off applied</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveCode}
                    className="text-green-500 hover:text-green-700 text-xs font-semibold transition-colors"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Enter the code"
                    className={`flex-1 bg-black/[0.04] border rounded-xl px-4 py-3 text-black placeholder-black/25 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/40 focus:border-[#f59e0b]/60 transition-all ${
                      codeStatus === "invalid" ? "border-red-300 bg-red-50" : "border-black/8"
                    }`}
                    value={codeInput}
                    onChange={(e) => { setCodeInput(e.target.value); setCodeStatus("idle"); }}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleApplyCode())}
                  />
                  <button
                    type="button"
                    onClick={handleApplyCode}
                    className="px-5 py-3 bg-black text-white font-bold text-sm rounded-xl hover:bg-black/80 transition-all flex-shrink-0"
                  >
                    Apply
                  </button>
                </div>
              )}
              {codeStatus === "invalid" && (
                <p className="text-red-500 text-xs mt-1.5 font-medium">Invalid promo code</p>
              )}
            </div>

            <button
              type="submit"
              className="w-full bg-[#f59e0b] hover:bg-amber-400 text-black font-black text-base py-4 rounded-2xl transition-all hover:shadow-xl hover:shadow-[#f59e0b]/25 hover:-translate-y-0.5 active:translate-y-0 mt-2"
            >
              Review & Pay →
            </button>
          </form>

        </motion.div>
      </div>
    </div>
  );
}
